import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';
import {
  Search,
  Download,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Star,
  MessageSquare,
  Flag,
  Eye,
  Calendar,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

// shadcn/ui components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  ReviewListItem,
  ReviewFilters,
  ReviewStatus,
  ReviewSortField,
  DEFAULT_REVIEW_FILTERS,
  PAGE_SIZE_OPTIONS,
  getRatingStars,
  formatReviewDate,
  calculateReviewAnalytics,
} from '@/types/reviews';

// ============================================================================
// Status Badge Mapping
// ============================================================================

function getStatusBadgeVariant(status: ReviewStatus) {
  const map: Record<ReviewStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    PENDING: 'secondary',
    APPROVED: 'default',
    REJECTED: 'outline',
    REPORTED: 'destructive',
  };
  return map[status];
}

function getRatingBadgeColor(rating: number) {
  if (rating >= 4) return 'text-green-600 dark:text-green-500';
  if (rating >= 3) return 'text-yellow-600 dark:text-yellow-500';
  return 'text-red-600 dark:text-red-500';
}

// ============================================================================
// Main Reviews Page Component
// ============================================================================

function ReviewsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  // State
  const [reviews, setReviews] = useState<ReviewListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ReviewFilters>(DEFAULT_REVIEW_FILTERS);
  const [selectedReview, setSelectedReview] = useState<ReviewListItem | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Response dialog state
  const [isResponseDialogOpen, setIsResponseDialogOpen] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  // Report dialog state
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportingReview, setReportingReview] = useState<string | null>(null);

  // Fetch reviews
  useEffect(() => {
    // Guard: don't fetch without a businessId -- the API requires it
    if (!operator || !selectedBusinessId) {
      setReviews([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();

    async function fetchReviews() {
      setLoading(true);
      setFetchError(null);
      try {
        const params = new URLSearchParams();
        params.set('businessId', selectedBusinessId!);

        const response = await apiFetch(`/api/operator/reviews?${params.toString()}`, { signal: controller.signal });

        if (controller.signal.aborted) return;
        if (response.ok) {
          const data = await response.json();
          // Transform API response to ReviewListItem format
          const transformedReviews: ReviewListItem[] = (data.data || []).map((review: Record<string, unknown>) => ({
            id: review.id,
            businessId: review.businessId,
            businessName: (review.business as Record<string, unknown>)?.name || 'Unknown',
            customerId: review.customerId,
            customerName: (review.customer as Record<string, unknown>)?.name || 'Unknown',
            customerPhone: (review.customer as Record<string, unknown>)?.phoneNumberE164,
            bookingId: review.bookingId,
            serviceName: (review.booking as Record<string, unknown>)?.service
              ? ((review.booking as Record<string, unknown>).service as Record<string, unknown>)?.name
              : 'Unknown Service',
            rating: review.rating as number,
            comment: review.comment as string | null,
            status: review.status as ReviewStatus,
            businessResponse: review.businessResponse as string | null,
            respondedAt: review.respondedAt ? new Date(review.respondedAt as string) : null,
            respondedBy: review.respondedBy as string | null,
            reportReason: review.reportReason as string | null,
            reportedAt: review.reportedAt ? new Date(review.reportedAt as string) : null,
            createdAt: new Date(review.createdAt as string),
          }));
          setReviews(transformedReviews);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        if (controller.signal.aborted) return;
        console.error('Failed to fetch reviews:', error);
        setFetchError(error instanceof Error ? error.message : 'Failed to fetch reviews');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    fetchReviews();
    return () => controller.abort();
  }, [selectedBusinessId, operator]);

  // Filter reviews
  const filteredReviews = useMemo(() => {
    let result = [...reviews];

    // Apply business filter
    if (selectedBusinessId) {
      result = result.filter((r) => r.businessId === selectedBusinessId);
    }

    // Apply search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.customerName.toLowerCase().includes(query) ||
          r.comment?.toLowerCase().includes(query) ||
          r.serviceName.toLowerCase().includes(query)
      );
    }

    // Apply rating filter
    if (filters.ratings.length > 0) {
      result = result.filter((r) => filters.ratings.includes(r.rating));
    }

    // Apply status filter
    if (filters.statuses.length > 0) {
      result = result.filter((r) => filters.statuses.includes(r.status));
    }

    // Apply response filter
    if (filters.hasResponse !== null) {
      result = result.filter((r) =>
        filters.hasResponse ? r.businessResponse !== null : r.businessResponse === null
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case 'createdAt':
          comparison = a.createdAt.getTime() - b.createdAt.getTime();
          break;
        case 'rating':
          comparison = a.rating - b.rating;
          break;
        case 'customerName':
          comparison = a.customerName.localeCompare(b.customerName);
          break;
        default:
          comparison = 0;
      }
      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [reviews, filters, selectedBusinessId]);

  // Calculate analytics
  const analytics = useMemo(() => calculateReviewAnalytics(filteredReviews), [filteredReviews]);

  // Paginate
  const paginatedReviews = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredReviews.slice(start, start + pageSize);
  }, [filteredReviews, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredReviews.length / pageSize);

  // Handlers
  const handleFilterChange = useCallback((key: keyof ReviewFilters, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const toggleRating = useCallback((rating: number) => {
    setFilters((prev) => ({
      ...prev,
      ratings: prev.ratings.includes(rating)
        ? prev.ratings.filter((r) => r !== rating)
        : [...prev.ratings, rating],
    }));
    setCurrentPage(1);
  }, []);

  const toggleStatus = useCallback((status: ReviewStatus) => {
    setFilters((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status],
    }));
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((field: ReviewSortField) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  }, []);

  const handleViewDetails = useCallback((review: ReviewListItem) => {
    setSelectedReview(review);
    setIsDetailOpen(true);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_REVIEW_FILTERS);
    setCurrentPage(1);
  }, []);

  // Response handlers
  const handleOpenResponseDialog = useCallback((reviewId: string, existingResponse?: string | null) => {
    setRespondingTo(reviewId);
    setResponseText(existingResponse || '');
    setIsResponseDialogOpen(true);
  }, []);

  const handleSubmitResponse = useCallback(async () => {
    if (!respondingTo || !responseText.trim()) return;

    try {
      const response = await apiFetch(`/api/operator/reviews/${respondingTo}/respond`, {
        method: 'POST',
        body: JSON.stringify({ response: responseText }),
      });

      if (response.ok) {
        // Update local state
        setReviews((prev) =>
          prev.map((r) =>
            r.id === respondingTo
              ? { ...r, businessResponse: responseText, respondedAt: new Date() }
              : r
          )
        );
        setIsResponseDialogOpen(false);
        setResponseText('');
        setRespondingTo(null);
      }
    } catch (error) {
      console.error('Failed to submit response:', error);
    }
  }, [respondingTo, responseText]);

  // Report handlers
  const handleOpenReportDialog = useCallback((reviewId: string) => {
    setReportingReview(reviewId);
    setReportReason('');
    setIsReportDialogOpen(true);
  }, []);

  const handleSubmitReport = useCallback(async () => {
    if (!reportingReview || !reportReason.trim()) return;

    try {
      const response = await apiFetch(`/api/operator/reviews/${reportingReview}/report`, {
        method: 'POST',
        body: JSON.stringify({ reason: reportReason }),
      });

      if (response.ok) {
        // Update local state
        setReviews((prev) =>
          prev.map((r) =>
            r.id === reportingReview
              ? { ...r, status: 'REPORTED' as ReviewStatus, reportReason, reportedAt: new Date() }
              : r
          )
        );
        setIsReportDialogOpen(false);
        setReportReason('');
        setReportingReview(null);
      }
    } catch (error) {
      console.error('Failed to submit report:', error);
    }
  }, [reportingReview, reportReason]);

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator) {
    return null;
  }

  const allStatuses: ReviewStatus[] = ['PENDING', 'APPROVED', 'REJECTED', 'REPORTED'];
  const allRatings = [5, 4, 3, 2, 1];

  return (
    <div className="p-6 space-y-6">
      {/* Error Banner */}
      {fetchError && (
        <div className="rounded-lg border border-destructive p-4 mb-4">
          <p className="text-destructive text-sm">{fetchError}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('reviews.title')}</h1>
          <p className="text-muted-foreground">
            {selectedBusiness ? selectedBusiness.name : t('common.allBusinesses')} · {t('reviews.reviewCount', { count: filteredReviews.length })}
          </p>
        </div>
        <Button variant="outline">
          <Download className="mr-2 h-4 w-4" />
          {t('common.export')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('reviews.totalReviews')}</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalReviews}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('reviews.averageRating')}</CardTitle>
            <Star className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              {analytics.averageRating.toFixed(1)}
              <span className="text-sm text-muted-foreground">/5</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('reviews.positiveReviews')}</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-500">
              {((analytics.ratingDistribution[4] + analytics.ratingDistribution[5]) / Math.max(analytics.totalReviews, 1) * 100).toFixed(0)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('reviews.pendingResponses')}</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {reviews.filter((r) => !r.businessResponse).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* Search */}
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('reviews.searchPlaceholder')}
                  value={filters.searchQuery}
                  onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={filters.hasResponse === null ? 'all' : filters.hasResponse ? 'yes' : 'no'}
                onValueChange={(value) =>
                  handleFilterChange('hasResponse', value === 'all' ? null : value === 'yes')
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('reviews.allStatuses')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('reviews.allStatuses')}</SelectItem>
                  <SelectItem value="yes">{t('reviews.hasResponse')}</SelectItem>
                  <SelectItem value="no">{t('reviews.noResponse')}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" onClick={resetFilters}>
                {t('common.reset')}
              </Button>
            </div>

            {/* Rating Filter Chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">{t('reviews.rating')}:</span>
              {allRatings.map((rating) => (
                <Button
                  key={rating}
                  variant={filters.ratings.includes(rating) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleRating(rating)}
                  className="h-7"
                >
                  {getRatingStars(rating as 1 | 2 | 3 | 4 | 5)} ({rating})
                </Button>
              ))}
            </div>

            {/* Status Filter Chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">{t('reviews.status')}:</span>
              {allStatuses.map((status) => {
                const statusLabels: Record<ReviewStatus, string> = {
                  PENDING: t('reviews.pending'),
                  APPROVED: t('reviews.approved'),
                  REJECTED: t('reviews.rejected'),
                  REPORTED: t('reviews.reported'),
                };
                return (
                  <Button
                    key={status}
                    variant={filters.statuses.includes(status) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleStatus(status)}
                    className="h-7"
                  >
                    {statusLabels[status]}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reviews Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('customerName')}
                >
                  <div className="flex items-center gap-1">
                    {t('reviews.customer')}
                    {filters.sortBy === 'customerName' && (
                      filters.sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>{t('reviews.service')}</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('rating')}
                >
                  <div className="flex items-center gap-1">
                    {t('reviews.rating')}
                    {filters.sortBy === 'rating' && (
                      filters.sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>{t('reviews.comment')}</TableHead>
                <TableHead>{t('reviews.status')}</TableHead>
                <TableHead>{t('reviews.response')}</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center gap-1">
                    {t('reviews.date')}
                    {filters.sortBy === 'createdAt' && (
                      filters.sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="text-right">{t('reviews.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedReviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center">
                    {t('reviews.noReviewsFound')}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReviews.map((review) => {
                  const statusLabels: Record<ReviewStatus, string> = {
                    PENDING: t('reviews.pending'),
                    APPROVED: t('reviews.approved'),
                    REJECTED: t('reviews.rejected'),
                    REPORTED: t('reviews.reported'),
                  };
                  return (
                    <TableRow key={review.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{review.customerName}</div>
                          <div className="text-sm text-muted-foreground">{review.customerPhone}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {review.serviceName}
                      </TableCell>
                      <TableCell>
                        <div className={`flex items-center gap-1 ${getRatingBadgeColor(review.rating)}`}>
                          <Star className="h-4 w-4 fill-current" />
                          <span className="font-medium">{review.rating}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <p className="truncate text-sm">
                          {review.comment || <span className="text-muted-foreground italic">{t('reviews.noComment')}</span>}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(review.status)}>
                          {statusLabels[review.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {review.businessResponse ? (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            {t('reviews.hasResponse')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            {t('reviews.noResponse')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{formatReviewDate(review.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleOpenResponseDialog(review.id, review.businessResponse)}
                            title={review.businessResponse ? t('reviews.editResponse') : t('reviews.respondToReview')}
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                          {review.status !== 'REPORTED' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleOpenReportDialog(review.id)}
                              title={t('reviews.reportReview')}
                            >
                              <Flag className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleViewDetails(review)}
                            title={t('reviews.viewDetails')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                {t('common.showingOf', {
                  from: (currentPage - 1) * pageSize + 1,
                  to: Math.min(currentPage * pageSize, filteredReviews.length),
                  total: filteredReviews.length,
                })}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {t('common.rows', { count: size })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  {t('common.previous')}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {t('common.pageOf', { current: currentPage, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Detail Sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {selectedReview && (
            <>
              <SheetHeader>
                <SheetTitle>{t('reviews.reviewDetails')}</SheetTitle>
                <SheetDescription>
                  {t('reviews.reviewNumber', { id: selectedReview.id.slice(-8) })}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Rating and Status */}
                <div className="flex items-center gap-4">
                  <div className={`flex items-center gap-2 text-2xl ${getRatingBadgeColor(selectedReview.rating)}`}>
                    <Star className="h-6 w-6 fill-current" />
                    <span className="font-bold">{selectedReview.rating}</span>
                  </div>
                  <Badge variant={getStatusBadgeVariant(selectedReview.status)}>
                    {selectedReview.status === 'PENDING' && t('reviews.pending')}
                    {selectedReview.status === 'APPROVED' && t('reviews.approved')}
                    {selectedReview.status === 'REJECTED' && t('reviews.rejected')}
                    {selectedReview.status === 'REPORTED' && t('reviews.reported')}
                  </Badge>
                </div>

                <Separator />

                {/* Customer Info */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">{t('reviews.customerInfo')}</h3>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="font-medium">{selectedReview.customerName}</div>
                      <div className="text-sm text-muted-foreground">{selectedReview.customerPhone}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Service Info */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">{t('reviews.serviceInfo')}</h3>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="font-medium">{selectedReview.serviceName}</div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          navigate({ to: `/operator/bookings/${selectedReview.bookingId}` });
                          setIsDetailOpen(false);
                        }}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {t('reviews.viewBooking')}
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Comment */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">{t('reviews.comment')}</h3>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm">
                        {selectedReview.comment || (
                          <span className="text-muted-foreground italic">{t('reviews.noComment')}</span>
                        )}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Business Response */}
                {selectedReview.businessResponse && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">{t('reviews.response')}</h3>
                    <Card>
                      <CardContent className="pt-4">
                        <p className="text-sm">{selectedReview.businessResponse}</p>
                        {selectedReview.respondedAt && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {t('reviews.respondedAt', { date: formatReviewDate(selectedReview.respondedAt) })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Report Info */}
                {selectedReview.status === 'REPORTED' && selectedReview.reportReason && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-destructive">{t('reviews.reportReason')}</h3>
                    <Card className="border-destructive">
                      <CardContent className="pt-4">
                        <p className="text-sm">{selectedReview.reportReason}</p>
                        {selectedReview.reportedAt && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {t('reviews.reportedAt', { date: formatReviewDate(selectedReview.reportedAt) })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Date */}
                <div className="text-sm text-muted-foreground">
                  {t('reviews.date')}: {formatReviewDate(selectedReview.createdAt)}
                </div>

                <Separator />

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => {
                      handleOpenResponseDialog(selectedReview.id, selectedReview.businessResponse);
                      setIsDetailOpen(false);
                    }}
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {selectedReview.businessResponse ? t('reviews.editResponse') : t('reviews.respondToReview')}
                  </Button>
                  {selectedReview.status !== 'REPORTED' && (
                    <Button
                      variant="destructive"
                      onClick={() => {
                        handleOpenReportDialog(selectedReview.id);
                        setIsDetailOpen(false);
                      }}
                    >
                      <Flag className="mr-2 h-4 w-4" />
                      {t('reviews.reportReview')}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Response Dialog */}
      <Dialog open={isResponseDialogOpen} onOpenChange={setIsResponseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('reviews.respondToReview')}</DialogTitle>
            <DialogDescription>
              {t('reviews.responsePlaceholder')}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder={t('reviews.responsePlaceholder')}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResponseDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmitResponse} disabled={!responseText.trim()}>
              {t('reviews.submitResponse')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('reviews.reportReview')}</DialogTitle>
            <DialogDescription>
              {t('reviews.reportReasonPlaceholder')}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder={t('reviews.reportReasonPlaceholder')}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReportDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleSubmitReport} disabled={!reportReason.trim()}>
              {t('reviews.submitReport')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/reviews')({
  component: ReviewsPage,
});
